import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { getInspections, deleteInspection } from "../../../services/api/qa/inspectionService";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { toast } from "react-hot-toast";

function InspectionList() {
    const [inspections, setInspections] = useState([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    // ✅ Delete Modal States
    const [showModal, setShowModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        fetchInspections();
    }, []);

    const fetchInspections = async () => {
        try {
            const res = await getInspections();
            setInspections(res);
        } catch (error) {
            console.error("Error fetching inspections:", error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Handle Delete Click
    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setShowModal(true);
    };

    // ✅ Confirm Delete
    const confirmDelete = async () => {
        try {
            await deleteInspection(selectedId);

            toast.success("Inspection deleted successfully");

            setShowModal(false);
            setSelectedId(null);

            fetchInspections(); // 🔥 refresh list from backend

        } catch (error) {
            console.error("Delete failed:", error);
        }
    };

    // ✅ Filtered Data
    const filteredInspections = useMemo(() => {
        return inspections.filter((inspection) => {
            const matchesSearch =
                `insp-${inspection.id}`
                    .toLowerCase()
                    .includes(search.toLowerCase()) ||
                String(inspection.checklist_id)
                    .toLowerCase()
                    .includes(search.toLowerCase()) ||
                inspection.status.toLowerCase().includes(search.toLowerCase());

            const matchesStatus =
                statusFilter === "all" || inspection.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [inspections, search, statusFilter]);

    return (
        <div className="p-6 relative">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Inspection List</h2>

                <Link
                    to="/inspections/create"
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                    + Create Inspection
                </Link>
            </div>

            {/* Search + Filter */}
            <div className="flex flex-col md:flex-row gap-3 mb-4">
                <input
                    type="text"
                    placeholder="Search by ID, Checklist, Status..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border px-3 py-2 rounded w-full md:w-1/3"
                />

                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border px-3 py-2 rounded w-full md:w-1/4"
                >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="rejected">Rejected</option>
                </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded shadow overflow-x-auto">
                <table className="min-w-full text-sm border">
                    <thead className="bg-gray-100 text-gray-600 text-xs uppercase">
                        <tr>
                            <th className="px-4 py-3 border">Slno</th>
                            <th className="px-4 py-3 border">Inspection ID</th>
                            <th className="px-4 py-3 border">Checklist ID</th>
                            <th className="px-4 py-3 border">Date</th>
                            <th className="px-4 py-3 border">Status</th>
                            <th className="px-4 py-3 border text-center">Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="5" className="text-center py-4">
                                    Loading...
                                </td>
                            </tr>
                        ) : filteredInspections.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center py-4">
                                    No inspections found
                                </td>
                            </tr>
                        ) : (
                            filteredInspections.map((inspection, index) => (
                                <tr key={inspection.id} className="hover:bg-gray-50 text-center">
                                    <td className="px-4 py-2 border ">
                                        {index + 1}
                                    </td>

                                    <td className="px-4 py-2 border">
                                        INSP-{String(inspection.id).padStart(3, "0")}
                                    </td>

                                    <td className="px-4 py-2 border">
                                        {inspection.checklist_id}
                                    </td>

                                    <td className="px-4 py-2 border">
                                        {new Date(
                                            inspection.inspection_date
                                        ).toLocaleDateString()}
                                    </td>

                                    <td className="px-4 py-2 border">
                                        <span
                                            className={`px-2 py-1 text-xs rounded ${inspection.status === "approved"
                                                ? "bg-green-100 text-green-600"
                                                : inspection.status === "pending"
                                                    ? "bg-yellow-100 text-yellow-600"
                                                    : "bg-red-100 text-red-600"
                                                }`}
                                        >
                                            {inspection.status}
                                        </span>
                                    </td>

                                    <td className="px-4 py-2 border">
                                        <div className="flex items-center justify-center gap-4">

                                            <Link
                                                to={`/inspections/view/${inspection.id}`}
                                                className="text-blue-600 hover:text-blue-800 transition"
                                            >
                                                <FaEye size={16} />
                                            </Link>

                                            <Link
                                                to={`/inspections/edit/${inspection.id}`}
                                                className="text-yellow-600 hover:text-yellow-800 transition"
                                            >
                                                <FaEdit size={16} />
                                            </Link>

                                            <button
                                                onClick={() => handleDeleteClick(inspection.id)}
                                                className="text-red-600 hover:text-red-800 transition"
                                            >
                                                <FaTrash size={16} />
                                            </button>

                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ✅ Delete Confirmation Modal */}
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
                        <h3 className="text-lg font-semibold mb-3 text-gray-800">
                            Confirm Delete
                        </h3>

                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to delete this inspection?
                        </p>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InspectionList;